import { IS_AUTH_SOURCE_OF_TRUTH_ENABLED } from '@/features/flags';
import { getOnPremApiBaseUrl } from '@/tauri/onprem/serverConfig';
import { isTauri } from '@/tauri/utils';
import { isOnPrem } from '@/utils/buildProfile';
import { getAuthContextForHttpClient } from '@/utils/http/getAuthTokenForHttpClient';
import { stripOnPremOptionalMetadataHeaders } from '@/utils/http/onPremOptionalMetadataHeaders';
import { redactHeadersInPlace } from '@/utils/http/redactHeaders';
import {
  createScribeHttpRejectedErrorWithRetry,
  rejectIfAuthRetryStale,
  rejectIfContextStale,
} from '@/utils/http/scribeHttpErrorHandler';
import { applyTracingHeaders } from '@/utils/http/tracing/tracingHeaders';
import { logger } from '@/utils/logger/logger';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { AuthRetryRevisionStaleError } from '@heidi/api/errors';
import axios, { type AxiosError } from 'axios';

// Docs: https://resident.global.api.heidihealth.com/api/v1/internal/template-gallery/docs#

const EKS_PROD_ENDPOINT = 'https://template-gallery.prod.glbl.aws.ext.heidihealth.com';

const defaultGlobalDomainsMap: Partial<Record<ENV_NAME, string>> = {
  [ENV_NAME.LOCALHOST]:
    process.env.NEXT_PUBLIC_USE_STAGING_GLOBAL_SERVICES === 'true'
      ? 'https://template-gallery.staging.glbl.aws.ext.heidihealth.com'
      : 'http://localhost:8000',
  [ENV_NAME.DEV]: 'https://template-gallery.dev.glbl.aws.ext.heidi.ai',
  [ENV_NAME.STAGING]: 'https://template-gallery.staging.glbl.aws.ext.heidihealth.com',
  [ENV_NAME.PROD]: EKS_PROD_ENDPOINT,
  [ENV_NAME.AZURE_DEV]:
    'https://hh-temp-container-02.braveglacier-0d519843.australiasoutheast.azurecontainerapps.io',
};

const getDefaultGlobalDomain = (): string => {
  if (isOnPrem && typeof window !== 'undefined') {
    // Tauri desktop: the WebView origin is an internal app origin, not the
    // hospital server — use the user-configured server URL instead.
    if (isTauri()) {
      const serverUrl = getOnPremApiBaseUrl();
      if (serverUrl) return serverUrl;
    }
    return window.location.origin;
  }
  if (process.env.NEXT_PUBLIC_GLOBAL_API_BASE_URL) {
    return process.env.NEXT_PUBLIC_GLOBAL_API_BASE_URL;
  }
  return defaultGlobalDomainsMap[getEnvironment()] ?? EKS_PROD_ENDPOINT;
};

export const defaultGlobalDomains = defaultGlobalDomainsMap;

export const getGlobalBaseURL = (): string => {
  if (isOnPrem && typeof window !== 'undefined') {
    if (isTauri()) {
      const serverUrl = getOnPremApiBaseUrl();
      if (serverUrl) return `${serverUrl}/api/v1/`;
    }
    return `${window.location.origin}/api/v1/`;
  }
  if (process.env.NEXT_PUBLIC_GLOBAL_API_BASE_URL) {
    return `${process.env.NEXT_PUBLIC_GLOBAL_API_BASE_URL}/api/v1/`;
  }
  return `${defaultGlobalDomainsMap[getEnvironment()] ?? EKS_PROD_ENDPOINT}/api/v1/`;
};

export const baseURL = `${getDefaultGlobalDomain()}/api/v1/`;

// Unauthenticated instance — public template-gallery endpoints that don't
// require user auth. Deliberately untouched by the cache migration: no auth
// header, no logout behaviour on 401, just header redaction + debug log on
// reject.
const instance = axios.create({
  baseURL,
});

instance.interceptors.request.use((config) => {
  applyTracingHeaders(config.headers);
  return config;
});

// Authenticated instance — endpoints that require the main user session
// token. The request interceptor reads through the shared
// getAuthTokenForHttpClient (suppression + external-launch-token presence gate +
// interim FHIR fallback + flag-gated cache read); the response handler is
// flag-gated.
//
// Response handling: flag-OFF must stay byte-equivalent to pre-cache behaviour: the simple
// `onRejectedCallback` (redact headers + `logger.debug` + reject) — no
// ACCOUNT_SUSPENDED redirect and no `debouncedExpiryLogout` on 401.
// Adopting the full scribeHTTP handler unconditionally would change
// flag-off behaviour, breaking that guarantee.
//
// Flag-ON adopts the retry-once handler — same pattern as scribeHTTP. The
// retry handler DOES fire debouncedExpiryLogout on a definitive 401; that
// behaviour change is deliberately scoped to flag-ON.
const authenticatedInstance = axios.create({
  baseURL,
});

const handleGlobalAuthenticatedHttpRejectedErrorWithRetry =
  createScribeHttpRejectedErrorWithRetry('globalHTTP-authenticated-retry');

authenticatedInstance.interceptors.request.use(async (config) => {
  const context = await getAuthContextForHttpClient();

  // Retry pin: reject rather than send a retried request whose refresh is
  // no longer current — mirrors the same check in create-scribe-http-clients.
  // Compares provider AND revision — a provider change that doesn't itself
  // advance the revision (e.g. a cookie-first flip on web) would otherwise
  // pass a revision-only comparison even though the pinned identity no
  // longer holds. Fails CLOSED on a missing context too (`context` is
  // `null`, which already mismatches any defined `__expectedAuthIdentity`)
  // and on a freshly-resolved context that is itself marked `stale`.
  if (
    config.__expectedAuthIdentity !== undefined &&
    (!context ||
      context.stale ||
      context.provider !== config.__expectedAuthIdentity.provider ||
      context.authRevision !== config.__expectedAuthIdentity.authRevision)
  ) {
    throw new AuthRetryRevisionStaleError();
  }

  if (config.headers) {
    config.headers.Authorization = context?.token ? `Bearer ${context.token}` : '';
    applyTracingHeaders(config.headers);
  }
  // Always replace request-auth metadata so a reused config cannot retain
  // stale values. Bearer presence is independent from identity context
  // because suppression can intentionally resolve no context.
  config.__authHadBearer = Boolean(context?.token);
  config.__authRecoveryContext = context
    ? {
        provider: context.provider,
        authRevision: context.authRevision,
        eligible: context.eligible,
        stale: context.stale,
      }
    : undefined;
  return config;
});

const onRejectedCallback = (error: AxiosError) => {
  redactHeadersInPlace(error.config?.headers);
  redactHeadersInPlace(error.response?.config?.headers);

  logger.debug(error.message);
  return Promise.reject(error);
};

instance.interceptors.response.use((response) => response, onRejectedCallback);
// Flag evaluated per-error — same per-error pattern as scribeHTTP.
// OpenFeature initializes after this module imports.
authenticatedInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (IS_AUTH_SOURCE_OF_TRUTH_ENABLED()) {
      return handleGlobalAuthenticatedHttpRejectedErrorWithRetry(error, (cfg) =>
        authenticatedInstance.request(cfg)
      );
    }
    // A stale-pin abort from the request interceptor above (a config that
    // still carries a prior retry's __expectedAuthIdentity, e.g. reused
    // across a flag flip) is a non-destructive cancellation, not a request
    // failure — reject it directly before any other classification.
    const staleRetryRejection = rejectIfAuthRetryStale(error);
    if (staleRetryRejection) return staleRetryRejection;
    // The flag can flip off between a request's dispatch (context-stamped,
    // flag-on) and its 401 arriving here. Revision-check first: a captured
    // context that's gone stale means the session has already been
    // replaced — reject the original error directly rather than let the
    // plain reject-callback path proceed against a superseded request.
    // `rejectIfContextStale` throws when stale; left un-caught here, that
    // rejection propagates as this handler's own — no `if` needed.
    await rejectIfContextStale(error);
    return onRejectedCallback(error);
  }
);

// On-prem desktop CORS adaptation (MOD-1490): strip optional metadata
// (tracing/device headers) the interceptors above add. Transforms run after
// request interceptors; no-op outside the on-prem desktop runtime, and the
// build-time `isOnPrem` lets cloud bundles drop the block.
if (isOnPrem) {
  [instance, authenticatedInstance].forEach((client) => {
    const transforms = client.defaults.transformRequest;
    client.defaults.transformRequest = [
      stripOnPremOptionalMetadataHeaders,
      ...(Array.isArray(transforms) ? transforms : transforms ? [transforms] : []),
    ];
  });
}

export const globalHttp = instance;
export const globalHttpAuthenticated = authenticatedInstance;
